import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './LoginPage.css';

const LoginPage = ({ setCurrentPage }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [isLocked, setIsLocked] = useState(false);
  const [configError, setConfigError] = useState(false);

  const saltHex = process.env.REACT_APP_SALT_HEX;
  const expectedHashHex = process.env.REACT_APP_EXPECTED_HASH_HEX;

  useEffect(() => {
    
    if (!saltHex || !expectedHashHex) {
      console.error(
        'Environment variables REACT_APP_SALT_HEX and REACT_APP_EXPECTED_HASH_HEX must be defined in .env file'
      );
      Swal.fire(
        'Configuration Error',
        'Application is not properly configured. Please contact support.',
        'error'
      );
      setConfigError(true);
      return;
    }

    const fromHome = sessionStorage.getItem('unauthorizedHome');
    if (fromHome === 'true') {
      Swal.fire('Access Denied', 'Please log in first.', 'error');
      sessionStorage.removeItem('unauthorizedHome');
    }

    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (isLoggedIn === 'true') {
      setCurrentPage('home');
    }
  }, [setCurrentPage, saltHex, expectedHashHex]);

  
  if (configError) return null;

  const salt = Uint8Array.from(saltHex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
  const expectedHash = Uint8Array.from(expectedHashHex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));

  async function hashPassword(pwd, salt) {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      enc.encode(pwd),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    const derived = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      key,
      256
    );
    return new Uint8Array(derived);
  }

  function timingSafeEqual(a, b) {
    if (a.length !== b.length) return false;
    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a[i] ^ b[i];
    }
    return result === 0;
  }

  const handleLogin = async () => {
    if (isLocked) return;

    if (!password) {
      Swal.fire('Missing Fields', 'Please enter password.', 'warning');
      return;
    }

    const computedHash = await hashPassword(password, salt);
    const isValid = timingSafeEqual(computedHash, expectedHash);

    if (isValid) {
      localStorage.setItem('isLoggedIn', 'true');
      setCurrentPage('home');
    } else {
      const newAttempts = attemptsLeft - 1;
      setAttemptsLeft(newAttempts);
      if (newAttempts > 0) {
        Swal.fire('Incorrect password!', `You have ${newAttempts} more chances to enter.`, 'error');
      } else {
        setIsLocked(true);
        Swal.fire('Too many attempts!', 'Access will be allowed after 5 minutes.', 'error');
        setTimeout(() => {
          setIsLocked(false);
          setAttemptsLeft(5);
        }, 5 * 60 * 1000);
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <h2 className="welcome-text">Welcome to</h2>
        <h1 className="brand-title">Brainster Math</h1>
        <img src="/brainster.png" alt="Brain" className="brain-img" />
      </div>

      <div className="login-right">
        <h2 className="login-title">Login</h2>
        <p className="login-subtitle">Enter your password to continue.</p>
        <form
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLocked) {
              e.preventDefault();
              handleLogin();
            }
          }}
          className="login-form"
        >
          <div className="input-group">
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLocked}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
                disabled={isLocked}
              >
                <img
                  src={showPassword ? '/images/show.svg' : '/images/hide.svg'}
                  alt={showPassword ? 'Hide password' : 'Show password'}
                  className="eye-icon"
                />
              </button>
            </div>
            <button className="login-btn" type="button" onClick={handleLogin} disabled={isLocked}>
              Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
